// Auth-critical helper (used by login/session/PDF)
import API_BASE from '@/lib/apiBase';
import { getAccessToken } from '@/lib/authBootstrap';
import { attachTransientMetadata, parseRetryAfterHeader } from '@/lib/transientApiErrors';
import { z } from 'zod';

export interface HttpOptions extends RequestInit {
	component?: string;
}

export async function http(input: RequestInfo, init?: HttpOptions): Promise<Response> {
	const url = typeof input === 'string' ? input : input.toString();
	const token = await getAccessToken();
	const headers = new Headers(init?.headers || {});
	
	// Always log for debugging auth issues
	if (import.meta.env.DEV) {
		console.log('[HTTP] Request:', { 
			url, 
			hasToken: !!token,
			method: init?.method || 'GET' 
		});
	}
	
	if (token) {
		headers.set('Authorization', `Bearer ${token}`);
		if (import.meta.env.DEV) console.log('[HTTP] Authorization header set');
	} else {
		console.error('[HTTP] ⚠️ NO TOKEN AVAILABLE - Request will likely fail with 401:', url);
		console.error('[HTTP] This is an authentication error - user may need to log in again');
	}
	
	// Prepend API_BASE if url doesn't already include a full URL (like getJson does)
	const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
	
	if (import.meta.env.DEV) {
		console.log('[HTTP] Full URL:', fullUrl);
		console.log('[HTTP] Header names:', Array.from(headers.keys()));
	}
	
	let res = await fetch(fullUrl, { ...init, headers, credentials: 'omit' });
	
	if (import.meta.env.DEV) {
		console.log('[HTTP] Response:', { 
			status: res.status, 
			statusText: res.statusText, 
			url: fullUrl,
			headers: Object.fromEntries(res.headers.entries())
		});
	}
	
	if (res.status === 401) {
		console.error('[HTTP] ❌ 401 UNAUTHORIZED - Token may be missing or invalid');
		console.error('[HTTP] Request URL:', fullUrl);
		console.error('[HTTP] Had token:', !!token);
	}
	
	if (res.status === 404) {
		// Dev-only one-time console for missing endpoints
		try {
			// @ts-ignore
			const g: any = window as any;
			g.__logged404 = g.__logged404 || new Set<string>();
			if (isDevEnv() && !g.__logged404.has(url)) {
				g.__logged404.add(url);
				console.warn('CLIENT_404', { url, method: (init?.method || 'GET').toUpperCase() });
			}
		} catch {}

		const payload = {
			method: (init?.method || 'GET').toUpperCase(),
			url: fullUrl,
			component: init?.component || null,
			apiBase: API_BASE,
			status: 404,
			referer: document.location.pathname
		};
		
		// Fire-and-forget report to backend
		fetch(`${API_BASE}/api/__client-404`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload)
		}).catch(() => {
			// Silently ignore errors in 404 reporting to prevent cascading issues
		});
	}
	
	return res;
}

// Centralized JSON getter with dev-safe 404 coercion
const LIST_ENDPOINT_ALLOWLIST = [
	'/api/campaigns/:id/media',
	'/api/campaigns/:id/stores',
	'/api/campaigns/:id/store-media',
	'/api/assignments',
	'/api/stores',
	'/api/stores/operator/:operatorId',
	'/api/campaigns',
	'/api/users/operators',
	'/api/rate-templates',
	'/api/remuneration/rates',
	'/api/store-format-operator-assignments',
	'/api/booking-cycles',
	'/api/campaign-types',
	'/api/campaigns/:id/store-activity',
	'/api/campaign-snapshots/:campaignId/media-summary',
	'/api/admin/format-discrepancies/:campaignId',
	'/api/locked-cycles/:cycleId/assignments',
];

const isAllowlistedList = (url: string): boolean => {
	const path = url.split('?')[0];
	return LIST_ENDPOINT_ALLOWLIST.some(pattern => {
		// very small matcher: replace :param with [^/]+ and test
		const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
		return regex.test(path);
	});
};

export class NotFoundError extends Error { constructor(public url: string) { super(`Not found: ${url}`); this.name = 'NotFoundError'; } }
export class ServerError extends Error {
  retryAfterMs?: number;

  constructor(public url: string, public status: number, retryAfterMs?: number) {
    super(`Server error ${status} for ${url}`);
    this.name = 'ServerError';
    this.retryAfterMs = retryAfterMs;
    attachTransientMetadata(this, status, retryAfterMs);
  }
}
export class ParseError extends Error { constructor(public context: string, public details?: unknown) { super(`Parse error for ${context}`); this.name = 'ParseError'; } }

const loggedOnce = new Set<string>();
function isDevEnv(): boolean {
  try {
    // Vite provides import.meta.env.DEV; fallback safely if types are missing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta: any = import.meta as any;
    return !!(meta && meta.env && meta.env.DEV);
  } catch {
    return false;
  }
}
const logOnce = (key: string, message: string, data?: unknown) => {
  if (isDevEnv() && !loggedOnce.has(key)) {
    loggedOnce.add(key);
    if (data === undefined) console.info(message); else console.info(message, data);
  }
};

// Use canonical auth source from authBootstrap.ts
// DO NOT create duplicate session caches here
async function resolveAccessToken(): Promise<string | null> {
  return await getAccessToken();
}

export async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
	const token = await getAccessToken();
	const headers = new Headers(init?.headers || {});
	if (token) headers.set('Authorization', `Bearer ${token}`);

	// Prepend API_BASE if url doesn't already include a full URL
	const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

	// Debug logging disabled for performance - only log errors
	// console.log(`[QUERY_DEBUG] Fetching URL: ${fullUrl} (original: ${url})`);

	// Add timestamp to prevent caching issues
	// OPTIMIZATION: Removed cache-busting to allow browser caching
	// const separator = fullUrl.includes('?') ? '&' : '?';
	// const noCacheUrl = `${fullUrl}${separator}_t=${Date.now()}`;
	const noCacheUrl = fullUrl;

	let res = await fetch(noCacheUrl, { ...init, headers, credentials: 'omit' });
	if (res.status === 404) {
		// Only log errors, not every request
		// console.log(`[QUERY_DEBUG] Fetch failed for ${noCacheUrl} with status 404`);
		if (isAllowlistedList(url)) {
			logOnce(`404-allowlisted-${url}`, `[getJson] 404 coerced to [] for ${url}`);
			return [] as unknown as T;
		}
		// singular resources -> null
		logOnce(`404-null-${url}`, `[getJson] 404 coerced to null for ${url}`);
		return null as unknown as T;
	}
	if (!res.ok) {
		// Only log errors
		if (res.status >= 500 || res.status === 429) {
			console.error(`[QUERY_ERROR] Fetch failed for ${noCacheUrl} with status ${res.status}`);
		}
		if (res.status >= 500 || res.status === 429) {
			const retryAfterMs = parseRetryAfterHeader(res.headers.get('Retry-After'));
			throw new ServerError(noCacheUrl, res.status, retryAfterMs);
		}
		const text = await res.text().catch(() => '');
		const error = new Error(`HTTP ${res.status} for ${noCacheUrl}: ${text}`);
		attachTransientMetadata(error as { status?: number; message?: string }, res.status);
		throw error;
	}
	const data = await res.json() as T;
	// Debug logging disabled for performance
	// console.log(`[QUERY_DEBUG] Fetch successful for ${noCacheUrl}`);
	return data;
}

export function parseJsonWithSchema<T>(json: unknown, schema: z.ZodSchema<T>, context: string, isListEndpoint: boolean): T {
	const parsed = schema.safeParse(json);
	if (parsed.success) return parsed.data;
	logOnce(`parse-${context}`, `[schema] Parse error for ${context}`, parsed.error);
	if (isListEndpoint) return [] as unknown as T;
	throw new ParseError(context, parsed.error);
}

// Type guards/coercers for safe rendering
export const asArray = <T>(v: unknown): T[] => Array.isArray(v) ? (v as T[]) : [];
export const asObject = <T extends object>(v: unknown): T => (v && typeof v === "object") ? (v as T) : {} as T;

/**
 * Paginated response shape from backend
 * [PHASE2_PAGINATION] Added for backend performance optimization
 */
export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/**
 * Check if response is a paginated response
 */
export function isPaginatedResponse<T>(response: unknown): response is PaginatedResponse<T> {
  return (
    response !== null &&
    typeof response === 'object' &&
    'data' in response &&
    Array.isArray((response as any).data) &&
    'totalCount' in response
  );
}

/**
 * Unwrap paginated response to get the data array
 * Handles both legacy (array) and new (paginated object) response formats
 * [PHASE2_PAGINATION] Backward-compatible helper for pagination rollout
 */
export function unwrapPaginatedResponse<T>(response: PaginatedResponse<T> | T[]): T[] {
  if (Array.isArray(response)) {
    return response;
  }
  if (isPaginatedResponse<T>(response)) {
    return response.data;
  }
  // Fallback for unexpected formats
  console.warn('[unwrapPaginatedResponse] Unexpected response format:', response);
  return [];
}

/**
 * Get JSON with automatic pagination unwrapping
 * Use this for endpoints that return paginated responses but where you want all data
 * [PHASE2_PAGINATION] Fetches with large pageSize to get all data in one request
 */
export async function getJsonUnwrapped<T>(url: string, init?: RequestInit): Promise<T[]> {
  // Add large pageSize to get all data (backward compatible - old endpoints ignore it)
  const separator = url.includes('?') ? '&' : '?';
  const paginatedUrl = `${url}${separator}pageSize=200`;
  const response = await getJson<PaginatedResponse<T> | T[]>(paginatedUrl, init);
  return unwrapPaginatedResponse(response);
}

export async function apiFetch(url: string, options: RequestInit = {}) {
  // Prepend API_BASE if url doesn't already include a full URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  if (import.meta.env.DEV) {
    console.log(`[API_FETCH] ===== API REQUEST =====`);
    console.log(`[API_FETCH] Original URL: ${url}`);
    console.log(`[API_FETCH] Full URL: ${fullUrl}`);
    console.log(`[API_FETCH] Method: ${options.method || 'GET'}`);
  }

  // Detect if body is FormData - don't set Content-Type for FormData (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }
  
  // Only set Content-Type if not FormData
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const token = await resolveAccessToken();
  if (import.meta.env.DEV) {
    console.log(`[API_FETCH] Has auth token: ${!!token}`);
    console.log(`[API_FETCH] Is FormData: ${isFormData}`);
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    if (import.meta.env.DEV) console.log(`[API_FETCH] Added Authorization header`);
  } else {
    if (import.meta.env.DEV) console.log(`[API_FETCH] No auth token found`);
  }

  // Add timestamp to prevent caching issues
  // OPTIMIZATION: Removed cache-busting to allow browser caching
  // const separator = fullUrl.includes('?') ? '&' : '?';
  // const noCacheUrl = `${fullUrl}${separator}_t=${Date.now()}`;
  const noCacheUrl = fullUrl;

  if (import.meta.env.DEV) console.log(`[API_FETCH] Making request to: ${noCacheUrl}`);
  const res = await fetch(noCacheUrl, { ...options, headers });

  if (import.meta.env.DEV) console.log(`[API_FETCH] Response status: ${res.status} ${res.statusText}`);

  if (!res.ok) {
    if (import.meta.env.DEV) console.log(`[API_FETCH] ❌ Request failed for ${noCacheUrl} with status ${res.status}`);
    // Log only for critical auth failures
    if (url.endsWith('/api/users/me')) {
      console.warn('[AUTH_SUPABASE] Auth failed for /api/users/me', { status: res.status, hasToken: !!token });
    }
    // Note: Session management is handled by authBootstrap.ts
  } else {
    if (import.meta.env.DEV) console.log(`[API_FETCH] ✅ Request successful for ${noCacheUrl}`);
  }

  return res;
}
